import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router';
import _ from 'lodash';

import { NOTE_ANCHOR_TYPES } from '../constants/notes';
import { generateDbId } from '../utils/utils';
import useMutateWithState from '../hooks/useMutateWithState';
import { apolloCacheUpdate } from '../utils/apollo';
import { DeleteNoteMutation, UpsertNoteMutation } from '../mutations';
import { NotesQuery } from '../queries';
import { MapContext } from './MapContext';
import { geojsonFeatureIdFromRecordIds, layerIsCustom } from '../utils/layers';
import { useFarmName } from '../utils/hooks';

const { FARM, LAYER, LAYER_FEATURE, SECTION } = NOTE_ANCHOR_TYPES;

const convertAnchorType = (note) => ({
  ...note,
  // Convert from DB anchor_type to frontend anchorType:
  anchorType: {
    'Pg::Farm': FARM,
    'Pg::Section': SECTION,
    'Pg::Layer': LAYER,
    'Pg::LayerFeature': LAYER_FEATURE,
  }[note.anchorType],
});

const INIT_STATE = {
  loading: false,
  mutationLoading: false,
  notesById: {},
  notesByAnchorId: {},
  anchorDataById: {},
  saveNote: async () => {},
  deleteNote: async () => {},
  status: null,
  mutateAndSetState: async () => {},
  currentNoteId: null,
  setCurrentNoteId: () => {},
};

const NotesContext = createContext(INIT_STATE);

/**
 * This hook returns mapping of anchorId -> { anchorName, anchorType } for all possible anchors.
 */
const useAnchorDataById = () => {
  const { farmId } = useParams();
  const farmName = useFarmName(!farmId);
  const {
    layers: { layers, allGeojsonFeatures, allLayerFeatureRecords },
    fields: { sectionsHash },
  } = useContext(MapContext);

  return useMemo(() => {
    const result = {};

    result[farmId] = { anchorName: farmName, anchorType: FARM };

    Object.entries(sectionsHash).forEach(([anchorId, { name: anchorName }]) => {
      result[anchorId] = { anchorName, anchorType: SECTION };
    });

    Object.entries(layers)
      .filter(([, layer]) => layerIsCustom(layer))
      .forEach(([anchorId, { displayName: anchorName }]) => {
        result[anchorId] = { anchorName, anchorType: LAYER };
      });

    Object.entries(allLayerFeatureRecords).forEach(
      ([anchorId, { layerId }]) => {
        const geojsonFeatureId = geojsonFeatureIdFromRecordIds(
          layerId,
          anchorId,
        );
        const feature = allGeojsonFeatures[geojsonFeatureId];
        const anchorName = feature?.properties?.name ?? 'Layer Feature';
        result[anchorId] = { anchorName, anchorType: LAYER_FEATURE };
      },
    );

    return result;
  }, [
    farmId,
    farmName,
    layers,
    allGeojsonFeatures,
    allLayerFeatureRecords,
    sectionsHash,
  ]);
};

export const NotesContextProvider = ({ children }) => {
  const { farmId } = useParams();

  // Get mapping of anchorId -> { anchorName, anchorType }
  const anchorDataById = useAnchorDataById();

  // Fetch all notes
  const { data, loading: queryLoading } = useQuery(NotesQuery, {
    variables: { farmId },
    onError: console.error,
  });
  const notesWithConvertedAnchorType = useMemo(
    () => (data?.farm?.notes ?? []).map(convertAnchorType),
    [data],
  );

  // Filter out notes for anchors that have been deleted
  const validNotes = useMemo(
    () =>
      notesWithConvertedAnchorType.filter(
        ({ anchorId }) => anchorId in anchorDataById,
      ),
    [anchorDataById, notesWithConvertedAnchorType],
  );

  // Transform fetched state into more usable formats
  const notesById = useMemo(() => _.keyBy(validNotes, 'id'), [validNotes]);
  const notesByAnchorId = useMemo(() => _.groupBy(validNotes, 'anchorId'), [
    validNotes,
  ]);

  // Keep track of status for notes mutations
  const [status, mutateAndSetState, resetMutationStatus] = useMutateWithState();

  /**
   * Keep track of current note id.
   *
   * States:
   *   if null: no note open/selected
   *   if 0: new note is open/selected
   *   otherwise: holds ID of selected note
   */
  const [currentNoteId, setCurrentNoteId] = useState(null);

  useEffect(() => {
    resetMutationStatus();
  }, [currentNoteId]); // eslint-disable-line react-hooks/exhaustive-deps -- resetMutationStatus is stable

  // After mutations, we'll update the farm.notes field of the NotesQuery cache:
  const apolloCacheUpdateSpec = {
    query: NotesQuery,
    variables: { farmId },
    path: 'farm.notes',
  };

  const [upsertNoteMutation, { loading: upsertLoading }] = useMutation(
    UpsertNoteMutation,
    {
      // After upsert, update the apollo cache w/ new data
      update: apolloCacheUpdate({
        ...apolloCacheUpdateSpec,
        updater: (notes, { mutationData: { upsertNote } }) =>
          notes
            .filter((n) => n.id !== upsertNote.result.id)
            .concat([upsertNote.result]),
      }),
    },
  );

  const [deleteNoteMutation, { loading: deleteLoading }] = useMutation(
    DeleteNoteMutation,
    {
      // After delete, update the apollo cache to remove data
      update: apolloCacheUpdate({
        ...apolloCacheUpdateSpec,
        updater: (notes, { mutationVariables }) =>
          notes.filter((n) => n.id !== mutationVariables.input.id),
      }),
    },
  );

  const saveNote = useCallback(
    async (note) => {
      // compose note payload, making sure to check nullable values
      const input = {
        ...note,
        title: note.title || 'Untitled',
        body: note.body || '',
        farmId,
        // remove __typename attribute which otherwise causes errors
        __typename: undefined,
      };

      if (!input.id) input.id = generateDbId('NOTE');

      const response = await mutateAndSetState(
        async () => upsertNoteMutation({ variables: { input } }),
        'Note saved!',
      );
      return response?.data?.upsertNote?.result;
    },
    [farmId, mutateAndSetState, upsertNoteMutation],
  );

  const updateNoteTitle = useCallback(
    async (noteId, title) => {
      const note = notesById[noteId];
      if (!note || !title) return null;
      const input = {
        ...note,
        title,
        farmId,
        // remove __typename attribute which otherwise causes errors
        __typename: undefined,
      };

      const response = await mutateAndSetState(async () =>
        upsertNoteMutation({ variables: { input } }),
      );
      return response?.data?.upsertNote?.result;
    },
    [farmId, mutateAndSetState, notesById, upsertNoteMutation],
  );

  const deleteNote = useCallback(
    async (note) => {
      const { id, updatedAt } = note;

      const response = await mutateAndSetState(
        async () =>
          deleteNoteMutation({ variables: { input: { id, updatedAt } } }),
        'Note deleted!',
      );
      return response?.data?.deleteNote?.result;
    },
    [deleteNoteMutation, mutateAndSetState],
  );

  const contextValue = useMemo(
    () => ({
      loading: queryLoading,
      mutationLoading: upsertLoading || deleteLoading,
      notesById,
      notesByAnchorId,
      anchorDataById,
      saveNote,
      updateNoteTitle,
      deleteNote,
      status,
      mutateAndSetState,
      currentNoteId,
      setCurrentNoteId,
    }),
    [
      anchorDataById,
      currentNoteId,
      deleteLoading,
      deleteNote,
      mutateAndSetState,
      notesByAnchorId,
      notesById,
      queryLoading,
      saveNote,
      status,
      updateNoteTitle,
      upsertLoading,
    ],
  );

  return (
    <NotesContext.Provider value={contextValue}>
      {children}
    </NotesContext.Provider>
  );
};

export const useNotesContext = () => useContext(NotesContext);
